import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Download } from 'lucide-react';

// Kurtarma kodlarının TEK gösterimi (sunucu düz metni bir daha vermez). Kopyala / indir.
// Giriş ekranında (tone="dark") ve Profil çekmecesinde (açık) aynı bileşen.

const Grid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
  border-radius: 8px;
  background: ${({ $tone }) => ($tone === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#F5F7FA')};
  border: 1px solid ${({ $tone }) => ($tone === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E8ECF0')};

  li {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
    font-size: 13px;
    letter-spacing: 0.5px;
    text-align: center;
    color: ${({ $tone, theme }) => ($tone === 'dark' ? '#fff' : theme.colors.text.primary)};
  }

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
`;

const Action = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${({ $tone }) => ($tone === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#D0D5DD')};
  background: transparent;
  cursor: pointer;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ $tone, theme }) => ($tone === 'dark' ? 'rgba(255, 255, 255, 0.8)' : theme.colors.primary.main)};

  &:hover { background: ${({ $tone }) => ($tone === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(2, 145, 239, 0.06)')}; }
`;

const Warning = styled.p`
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ $tone }) => ($tone === 'dark' ? 'rgba(255, 255, 255, 0.75)' : '#6B7280')};
`;

export default function RecoveryCodesPanel({ codes, tone = 'light', accountLabel }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const text = (codes || []).join('\n');

  const copyAll = () => {
    Promise.resolve(navigator.clipboard?.writeText(text))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  const download = () => {
    const header = `${t('mfa.recovery.fileHeader')}${accountLabel ? ` — ${accountLabel}` : ''}\n\n`;
    const blob = new Blob([header + text + '\n'], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'flexbee-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <Warning $tone={tone}>{t('mfa.recovery.warning')}</Warning>
      <Grid $tone={tone} data-testid="mfa-recovery-codes">
        {(codes || []).map((code) => <li key={code}>{code}</li>)}
      </Grid>
      <Actions>
        <Action type="button" $tone={tone} onClick={copyAll}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? t('mfa.copied') : t('mfa.recovery.copy')}
        </Action>
        <Action type="button" $tone={tone} onClick={download}>
          <Download size={14} />
          {t('mfa.recovery.download')}
        </Action>
      </Actions>
    </div>
  );
}
